import React from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { MembersFieldDto } from "../../types/membersTypes";
import { ContactPersonDetailsDto } from "../../types/companyTypes";
import { ActivityRecurrenceFrequency, ActivityStatus, ActivityType } from "../../types/activityTypes";
import NumberField from "../general/NumberField";

interface ActivityFormDialogProps {
  open: boolean;
  title: string;
  submitLabel: string;
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  contactPersonOptions?: ContactPersonDetailsDto[];
  selectedContactPersonId?: number | "";
  onSelectedContactPersonChange?: (value: number | "") => void;
  contactPersonError?: boolean;
  contactPersonName?: string;
  companyName?: string;
  activityType: ActivityType;
  onActivityTypeChange: (value: ActivityType) => void;
  activityTypeError: boolean;
  activityTitle: string;
  onActivityTitleChange: (value: string) => void;
  activityTitleError: boolean;
  activityDescription: string;
  onActivityDescriptionChange: (value: string) => void;
  responsibleUserId: number | "";
  onResponsibleUserChange: (value: number | "") => void;
  responsibleUserError: boolean;
  responsibleUserLabel: string;
  members: MembersFieldDto[];
  activityDeadline: Dayjs | null;
  onActivityDeadlineChange: (value: Dayjs | null) => void;
  deadlineError: boolean;
  idPrefix: string;
  statusOptions?: {
    value: ActivityStatus;
    onChange: (value: ActivityStatus) => void;
    options: Array<{ value: ActivityStatus; label: string }>;
    disabled?: boolean;
  };
  waitingReasonOptions?: {
    value: string;
    onChange: (value: string) => void;
    error: boolean;
  };
  createRecurrenceOptions?: {
    isRecurring: boolean;
    onIsRecurringChange: (value: boolean) => void;
    recurrenceFrequency: ActivityRecurrenceFrequency;
    onRecurrenceFrequencyChange: (value: ActivityRecurrenceFrequency) => void;
    recurrenceCount: number;
    onRecurrenceCountChange: (value: number) => void;
    recurrenceCountError: boolean;
  };
}

/**
 * Shared dialog for creating and editing activities.
 */
const ActivityFormDialog = ({
  open,
  title,
  submitLabel,
  onClose,
  onSubmit,
  isSubmitting,
  contactPersonOptions,
  selectedContactPersonId,
  onSelectedContactPersonChange,
  contactPersonError,
  contactPersonName,
  companyName,
  activityType,
  onActivityTypeChange,
  activityTypeError,
  activityTitle,
  onActivityTitleChange,
  activityTitleError,
  activityDescription,
  onActivityDescriptionChange,
  responsibleUserId,
  onResponsibleUserChange,
  responsibleUserError,
  responsibleUserLabel,
  members,
  activityDeadline,
  onActivityDeadlineChange,
  deadlineError,
  idPrefix,
  statusOptions,
  waitingReasonOptions,
  createRecurrenceOptions,
}: ActivityFormDialogProps) => {
  const showWaitingReason = statusOptions?.value === ActivityStatus.WAITING_FOR_CUSTOMER && waitingReasonOptions;
  const selectableContactPersonOptions =
    contactPersonOptions?.filter(
      (contactPerson) =>
        contactPerson.crmContactAllowed !== false && contactPerson.crmContactStatus !== "NO_FURTHER_CONTACT_DESIRED"
    ) ?? [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} marginTop={0}>
          {contactPersonOptions && onSelectedContactPersonChange ? (
            <Grid item xs={12}>
              <FormControl fullWidth error={contactPersonError}>
                <InputLabel id={`${idPrefix}-contact-person-label`}>Ansprechperson</InputLabel>
                <Select
                  id={`${idPrefix}-contact-person`}
                  name={`${idPrefix}-contact-person`}
                  labelId={`${idPrefix}-contact-person-label`}
                  label="Ansprechperson"
                  value={selectedContactPersonId ?? ""}
                  onChange={(event) => {
                    const { value } = event.target;
                    onSelectedContactPersonChange(value === "" ? "" : Number(value));
                  }}
                >
                  <MenuItem value="" />
                  {selectableContactPersonOptions.map((contactPerson) => (
                    <MenuItem value={contactPerson.contactPersonId} key={contactPerson.contactPersonId}>
                      {contactPerson.name}
                    </MenuItem>
                  ))}
                </Select>
                {contactPersonError ? <FormHelperText>Bitte wähle eine Ansprechperson aus.</FormHelperText> : null}
              </FormControl>
            </Grid>
          ) : contactPersonName !== undefined ? (
            <Grid item xs={12}>
              <TextField
                id={`${idPrefix}-contact-person`}
                name={`${idPrefix}-contact-person`}
                fullWidth
                label="Ansprechperson"
                value={contactPersonName}
                disabled
                variant="outlined"
              />
            </Grid>
          ) : null}
          {companyName !== undefined && (
            <Grid item xs={12}>
              <TextField
                id={`${idPrefix}-company`}
                name={`${idPrefix}-company`}
                fullWidth
                label="Unternehmen"
                value={companyName}
                disabled
                variant="outlined"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControl fullWidth error={activityTypeError}>
              <InputLabel id={`${idPrefix}-type-label`}>Aktivitätstyp</InputLabel>
              <Select
                id={`${idPrefix}-type`}
                name={`${idPrefix}-type`}
                labelId={`${idPrefix}-type-label`}
                label="Aktivitätstyp"
                value={activityType}
                onChange={(event) => onActivityTypeChange(event.target.value as ActivityType)}
              >
                <MenuItem value={ActivityType.CALL}>Anruf</MenuItem>
                <MenuItem value={ActivityType.MAIL}>E-Mail</MenuItem>
                <MenuItem value={ActivityType.MEETING}>Meeting</MenuItem>
                <MenuItem value={ActivityType.OTHER}>Sonstiges</MenuItem>
              </Select>
              {activityTypeError ? <FormHelperText>Bitte wähle einen Aktivitätstyp aus.</FormHelperText> : null}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth error={responsibleUserError}>
              <InputLabel id={`${idPrefix}-responsible-user-label`}>{responsibleUserLabel}</InputLabel>
              <Select
                id={`${idPrefix}-responsible-user`}
                name={`${idPrefix}-responsible-user`}
                labelId={`${idPrefix}-responsible-user-label`}
                label={responsibleUserLabel}
                value={responsibleUserId}
                onChange={(event) => {
                  const { value } = event.target;
                  onResponsibleUserChange(value === "" ? "" : Number(value));
                }}
              >
                <MenuItem value="" />
                {members.map((member) => (
                  <MenuItem value={member.memberId} key={member.memberId}>
                    {member.firstname} {member.lastname}
                  </MenuItem>
                ))}
              </Select>
              {responsibleUserError ? (
                <FormHelperText>Bitte wähle eine verantwortliche Person aus.</FormHelperText>
              ) : null}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <DatePicker
              sx={{ width: "100%" }}
              label="Deadline"
              value={activityDeadline}
              slotProps={{
                textField: {
                  id: `${idPrefix}-deadline`,
                  name: `${idPrefix}-deadline`,
                  variant: "outlined",
                  error: deadlineError,
                  helperText: deadlineError ? "Bitte wähle eine Deadline aus." : undefined,
                },
              }}
              onChange={(value) => onActivityDeadlineChange(value)}
            />
          </Grid>
          {statusOptions && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id={`${idPrefix}-status-label`}>Status</InputLabel>
                <Select
                  id={`${idPrefix}-status`}
                  name={`${idPrefix}-status`}
                  labelId={`${idPrefix}-status-label`}
                  label="Status"
                  value={statusOptions.value}
                  onChange={(event) => statusOptions.onChange(event.target.value as ActivityStatus)}
                  disabled={statusOptions.disabled}
                >
                  {statusOptions.options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {showWaitingReason && (
            <Grid item xs={12}>
              <TextField
                id={`${idPrefix}-waiting-reason`}
                name={`${idPrefix}-waiting-reason`}
                fullWidth
                multiline
                rows={3}
                label="Worauf wird gewartet?"
                variant="outlined"
                value={waitingReasonOptions.value}
                error={waitingReasonOptions.error}
                helperText={
                  waitingReasonOptions.error
                    ? "Bitte gib an, worauf oder warum auf den Kunden gewartet wird."
                    : "Diese Notiz wird später in den Aktivitätsdetails angezeigt."
                }
                onChange={(event) => waitingReasonOptions.onChange(event.target.value)}
              />
            </Grid>
          )}
          {createRecurrenceOptions && (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={`${idPrefix}-is-recurring`}
                      name={`${idPrefix}-is-recurring`}
                      checked={createRecurrenceOptions.isRecurring}
                      onChange={(event) => createRecurrenceOptions.onIsRecurringChange(event.target.checked)}
                    />
                  }
                  label="Wiederkehrende Aktivität"
                />
              </Grid>
              {createRecurrenceOptions.isRecurring && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id={`${idPrefix}-recurrence-frequency-label`}>Wiederholung</InputLabel>
                      <Select
                        id={`${idPrefix}-recurrence-frequency`}
                        name={`${idPrefix}-recurrence-frequency`}
                        labelId={`${idPrefix}-recurrence-frequency-label`}
                        label="Wiederholung"
                        value={createRecurrenceOptions.recurrenceFrequency}
                        onChange={(event) =>
                          createRecurrenceOptions.onRecurrenceFrequencyChange(
                            event.target.value as ActivityRecurrenceFrequency
                          )
                        }
                      >
                        <MenuItem value={ActivityRecurrenceFrequency.WEEKLY}>Wöchentlich</MenuItem>
                        <MenuItem value={ActivityRecurrenceFrequency.MONTHLY}>Monatlich</MenuItem>
                        <MenuItem value={ActivityRecurrenceFrequency.QUARTERLY}>Quartalsweise</MenuItem>
                        <MenuItem value={ActivityRecurrenceFrequency.SEMIANNUAL}>Halbjährlich</MenuItem>
                        <MenuItem value={ActivityRecurrenceFrequency.YEARLY}>Jährlich</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <NumberField
                      id={`${idPrefix}-recurrence-count`}
                      name={`${idPrefix}-recurrence-count`}
                      fullWidth
                      label="Anzahl Termine"
                      variant="outlined"
                      value={createRecurrenceOptions.recurrenceCount}
                      error={createRecurrenceOptions.recurrenceCountError}
                      helperText={
                        createRecurrenceOptions.recurrenceCountError ? "Die Anzahl muss mindestens 1 sein." : undefined
                      }
                      inputProps={{ min: 1 }}
                      onChange={(event) => createRecurrenceOptions.onRecurrenceCountChange(Number(event.target.value))}
                    />
                  </Grid>
                </>
              )}
            </>
          )}
          <Grid item xs={12}>
            <TextField
              id={`${idPrefix}-title`}
              name={`${idPrefix}-title`}
              fullWidth
              label="Titel"
              variant="outlined"
              required
              value={activityTitle}
              error={activityTitleError}
              helperText={activityTitleError ? "Bitte gib einen Titel ein." : undefined}
              onChange={(event) => onActivityTitleChange(event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id={`${idPrefix}-description`}
              name={`${idPrefix}-description`}
              fullWidth
              multiline
              rows={4}
              label="Beschreibung"
              variant="outlined"
              value={activityDescription}
              onChange={(event) => onActivityDescriptionChange(event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Abbrechen
        </Button>
        <Button onClick={onSubmit} color="primary" disabled={isSubmitting}>
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivityFormDialog;
